import axios from 'axios'
import { apiClient, API_BASE_URL, API_TIMEOUT } from '@/lib/api'
import type { StandardResponse, MessageResponse, ErrorResponse } from '@/types/api-response'
import { isErrorResponse, extractData } from '@/types/api-response'

/**
 * Admin login request payload
 */
export interface LoginRequest {
  email: string
  password: string
}

/**
 * Admin login response from backend
 * Contains JWT token, refresh token, and user info
 */
export interface LoginResponse {
  token: string
  refreshToken: string
  user: {
    id: string
    email: string
    fullName: string
    roles: string[]
    permissions: string[]
  }
}

/**
 * Token refresh request payload
 */
export interface RefreshTokenRequest {
  refreshToken: string
}

/**
 * Token refresh response from backend
 * Contains new JWT token and rotated refresh token
 */
export interface RefreshTokenResponse {
  token: string
  refreshToken: string
}

/**
 * Admin authentication service
 * Handles login, logout, token refresh, and session verification
 *
 * All endpoints return StandardResponse<T> or MessageResponse formats
 */
export const adminAuthService = {
  /**
   * Authenticates admin user with email and password
   * @param credentials - Email and password
   * @returns Login response with tokens and user info
   * @throws Error if credentials are invalid or response is error
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<StandardResponse<LoginResponse> | ErrorResponse>(
      '/api/v1/admin/auth/login',
      credentials
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractData(response.data)
  },

  /**
   * Refreshes access token using refresh token (with token rotation)
   *
   * Deliberately uses a bare `axios.post` rather than the shared `apiClient`: this one call must
   * never re-enter `apiClient`'s 401 interceptor. When the refresh token has expired too the
   * server answers 401, and on the shared instance that response was parked in the interceptor's
   * `failedQueue` — queued behind the very refresh it was the answer to. Nothing ever released
   * the queue, so the session was never cleared and the console spun on a loading state forever.
   * The client app refreshes the same way, for the same reason.
   *
   * The endpoint is `[AllowAnonymous]`, so no Authorization header is needed — which is just as
   * well, since the access token is expired by definition at this point.
   *
   * @param request - Refresh token request
   * @returns New access token and rotated refresh token
   * @throws Error if refresh token is invalid or expired
   */
  async refreshToken(request: RefreshTokenRequest): Promise<RefreshTokenResponse> {
    const response = await axios.post<StandardResponse<RefreshTokenResponse> | ErrorResponse>(
      `${API_BASE_URL}/api/v1/admin/auth/refresh`,
      request,
      {
        timeout: API_TIMEOUT,
        headers: { 'Content-Type': 'application/json' },
      }
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }

    return extractData(response.data)
  },

  /**
   * Verifies current admin session
   * @throws Error if session is invalid or expired
   */
  async verify(): Promise<void> {
    const response = await apiClient.get<StandardResponse<unknown> | MessageResponse | ErrorResponse>(
      '/api/v1/admin/auth/verify'
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }
  },

  /**
   * Logs out admin user and revokes refresh token
   * @throws Error if logout fails
   */
  async logout(refreshToken: string | null): Promise<void> {
    const response = await apiClient.post<MessageResponse | ErrorResponse>(
      '/api/v1/admin/auth/logout',
      { refreshToken }
    )

    if (isErrorResponse(response.data)) {
      throw new Error(response.data.error.message)
    }
  },
}
